import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Box, Button, Chip, Typography } from '@mui/material';
import CakeOutlinedIcon from '@mui/icons-material/CakeOutlined';
import EmojiEmotionsOutlinedIcon from '@mui/icons-material/EmojiEmotionsOutlined';
import { orchard } from '../../theme';

/* «Дни рождения»: children with a birthday in the next `days` days — today's
   first, with a shortcut to birthday stickers. Hidden when there are none.
   all: the whole school (admin dashboard). */
export default function BirthdayBoard({ days = 30, all = false, en = false }) {
  const [list, setList] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`/api/birthdays?days=${days}${all ? '&all=1' : ''}`, {
      credentials: 'include',
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setList(Array.isArray(d) ? d : []))
      .catch(() => setList([]));
  }, [days, all]);

  if (!list || !list.length) return null;
  const today = list.filter((k) => k.inDays === 0);

  return (
    <Box
      data-reveal
      sx={(theme) => ({
        mb: 3,
        p: { xs: 1.75, md: 2.25 },
        borderRadius: '20px',
        border: '1px solid',
        borderColor:
          theme.palette.mode === 'dark'
            ? 'rgba(242,182,74,0.3)'
            : 'rgba(214,150,30,0.28)',
        background:
          theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, rgba(242,182,74,0.08), rgba(242,89,114,0.06))'
            : 'linear-gradient(135deg, #fff8ea 0%, #fff1f3 100%)',
      })}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
        <CakeOutlinedIcon sx={{ color: orchard.honey }} />
        <Typography sx={{ fontWeight: 800, mr: 'auto' }}>
          {en ? 'Birthdays' : 'Дни рождения'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {en ? `next ${days} days` : `ближайшие ${days} дней`}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          overflowX: 'auto',
          pb: 0.5,
          scrollbarWidth: 'thin',
        }}
      >
        {list.map((k) => {
          const isToday = k.inDays === 0;
          return (
            <Box
              key={k.id}
              role="link"
              tabIndex={0}
              onClick={() => navigate(`/student/${k.id}`)}
              onKeyDown={(e) =>
                e.key === 'Enter' && navigate(`/student/${k.id}`)
              }
              sx={{
                flex: '0 0 auto',
                width: 132,
                p: 1.25,
                borderRadius: '16px',
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: isToday ? orchard.honey : 'divider',
                boxShadow: isToday
                  ? '0 8px 22px -12px rgba(214,150,30,0.7)'
                  : 'none',
                display: 'grid',
                justifyItems: 'center',
                gap: 0.4,
                textAlign: 'center',
                cursor: 'pointer',
              }}
            >
              <Avatar
                src={k.photo || undefined}
                alt=""
                sx={{
                  width: 56,
                  height: 56,
                  fontWeight: 800,
                  border: '3px solid',
                  borderColor: isToday ? orchard.honey : 'transparent',
                }}
              >
                {k.firstName[0]}
              </Avatar>
              <Typography
                noWrap
                sx={{ fontWeight: 800, fontSize: '0.88rem', maxWidth: '100%' }}
              >
                {k.firstName}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.76rem',
                  fontWeight: isToday ? 800 : 600,
                  color: isToday ? orchard.honey : 'text.secondary',
                }}
              >
                {isToday
                  ? en
                    ? 'Today 🎉'
                    : 'Сегодня 🎉'
                  : `${k.dateRu} · ${k.when}`}
              </Typography>
              <Chip
                size="small"
                label={
                  isToday
                    ? k.age
                    : en
                      ? `turns ${k.turns}`
                      : `исполнится ${k.turns}`
                }
                sx={{ height: 20, fontSize: '0.68rem' }}
              />
              {all && k.teacherName && (
                <Typography
                  noWrap
                  variant="caption"
                  color="text.secondary"
                  sx={{ maxWidth: '100%' }}
                >
                  {k.groups[0] || k.teacherName}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
      {today.length > 0 && (
        <Button
          size="small"
          startIcon={<EmojiEmotionsOutlinedIcon />}
          onClick={() =>
            navigate(
              `/images?tab=stickers&people=${encodeURIComponent(
                today.map((k) => `student:${k.id}`).join(',')
              )}`
            )
          }
          sx={{ mt: 1 }}
        >
          {en
            ? 'Birthday stickers'
            : today.length === 1
              ? `Наклейки ко дню рождения: ${today[0].firstName}`
              : 'Наклейки для именинников'}
        </Button>
      )}
    </Box>
  );
}
